import { Box, Grid, Grid2 } from '@mui/material'
import { styled, Theme } from '@mui/material/styles'
import { borderColor, borderRadius } from '@mui/system'

export const PicContainer = styled(Grid)(({ theme }) => ({
  width: 52,
  height: 52,
  borderRadius: 0,
  backgroundColor: theme.palette.card.primary,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}))

export const Avatar = styled(Grid)(({ theme }) => ({
  width: 52,
  height: 52,
  borderRadius: '50%',
  backgroundColor: theme.palette.card.primary,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: theme.palette.text.primary,
  fontSize: '1rem',

  '&[data-size="sm"]': {
    width: 24,
    height: 24,
    fontSize: '0.875rem',
    '@media (max-height: 500px)': {
      width: 24,
      height: 24,
      minWidth: 24,
      minHeight: 24
    }
  },

  '&[data-size="md"]': {
    width: 40,
    height: 40,
    fontSize: '0.875rem',
    '@media (max-height: 500px)': {
      width: 24,
      height: 24,
      minWidth: 24,
      minHeight: 24
    }
  },

  '&[data-size="xs"]': {
    width: 20,
    height: 20,
    fontSize: '0.75rem'
  },

  '&[data-size="xxs"]': {
    width: 16,
    height: 16,
    fontSize: '0.75rem'
  },

  '&[data-asgroup="true"]': {
    alignItems: 'flex-end',
    '& svg': {
      color: '#FFF',
      width: '75%',
      height: '75%'
    }
  },

  '&[data-colored="true"]': {
    color: '#FFFFFF'
  },

  '& img': {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: 'cover'
  },

  '&[data-noradius="true"]': {
    '& img': {
      borderRadius: 0,
      objectFit: 'cover'
    }
  }
}))

export const MultiAccountBadge = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',

  '&[data-dir="ltr"]': {
    direction: 'ltr'
  },

  '&[data-dir="rtl"]': {
    direction: 'rtl'
  },

  '& > div + div': {
    marginInlineStart: theme.spacing(-5),
    '@media (max-height: 500px)': {
      marginInlineStart: theme.spacing(-3)
    }
  }
}))

export const InviteLinkBadge = styled(Grid2)(({ theme }) => ({
  width: '100%',
  background: 'var(--color-border-soft)',
  fontSize: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  flexDirection: 'column',

  borderRadius: 10,

  color: theme.palette.text.primary
}))

export const InviteLinkContainer = styled(InviteLinkBadge)(({ theme }) => ({
  zIndex: 11,

  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  border: '1px',
  borderStyle: 'solid',
  borderColor: theme.palette.border.primary,
  background: theme.palette.card.primary
}))

export const CopyLinkButton = styled('button')(({ theme }) => ({
  height: '100%',

  color: theme.palette.button.action.contrastText,
  display: 'flex',
  alignItems: 'center',
  padding: '0 0.625rem',
  borderRadius: '0 0.55rem 0.55rem 0',
  cursor: 'pointer',
  fontWeight: 500,
  border: 'none',
  outline: 'none',
  backgroundColor: theme.palette.button.action.main,

  '&:hover': {
    backgroundColor: theme.palette.button.action.hover
  },
  '&:active': {
    backgroundColor: theme.palette.button.action.active
  },

  '&:disabled': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? theme.palette.action.disabledBackground
        : theme.palette.button.action.disabled,
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.text.disabled
        : theme.palette.button.action.contrastText,
    cursor: 'not-allowed'
  }
}))

interface LinkContentProps {
  allowScroll?: boolean
  theme: Theme
}

export const LinkContent = styled('div')((props: LinkContentProps) => ({
  flex: 1,
  padding: '0 0.625rem',
  whiteSpace: 'nowrap',
  overflow: props.allowScroll ? undefined : 'hidden',
  wordBreak: props.allowScroll ? undefined : 'break-all',
  textOverflow: props.allowScroll ? undefined : 'ellipsis',
  overflowX: props.allowScroll ? 'auto' : undefined,
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    width: '0px',
    height: '0px'
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'var(--color-border-soft)'
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'var(--color-text-muted)'
  }
}))

interface ProBadgeProps {
  badgesize: number
}

export const ProBadgeBox = styled(Box)(
  ({ badgesize, theme }: ProBadgeProps & { theme: Theme }) => ({
    display: 'flex',
    '& svg': {
      width: badgesize,
      height: 'auto',
      maxWidth: badgesize,
      color: theme.palette.input.placeholder
    }
  })
)
